import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Conversation {
  id: string;
  request_code: string;
  subject: string;
  status: string;
  created_at: string;
}

interface ChatMessage {
  id: string;
  content: string;
  is_staff: boolean;
  created_at: string;
  attachment_url: string | null;
  attachment_name: string | null;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return 'Hôm nay';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
}

export default function ChatWindow({ bare = false }: { bare?: boolean }) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [isOnline, setIsOnline] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const apiUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/chat-support`;

  const fetchConversation = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError('Vui lòng đăng nhập để sử dụng chat');
        setLoading(false);
        return;
      }

      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'get_conversation' }),
      });
      const result = await resp.json();
      if (!resp.ok || result.error) {
        throw new Error(result.error || 'Không thể tải cuộc trò chuyện');
      }
      setConversation(result.conversation);
      if (result.conversation?.id) {
        await fetchMessages(result.conversation.id, token);
      }
    } catch (e: any) {
      setError(e.message || 'Không thể kết nối chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (requestId: string, token: string) => {
    const resp = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'get_messages', request_id: requestId }),
    });
    const result = await resp.json();
    if (resp.ok && result.messages) {
      setMessages(result.messages);
    }
  };

  const sendMessage = async () => {
    if (!conversation?.id || !input.trim() || sending) return;
    setSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Unauthorized');

      const text = input.trim();
      setInput('');

      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'send_message', request_id: conversation.id, content: text }),
      });
      const result = await resp.json();
      if (!resp.ok || result.error) {
        throw new Error(result.error || 'Không thể gửi tin nhắn');
      }
      if (result.message) {
        setMessages((prev) => [...prev, result.message]);
      }
    } catch (e: any) {
      setError(e.message || 'Gửi tin nhắn thất bại');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial load
  useEffect(() => {
    fetchConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!conversation?.id) return;
    const channel = supabase
      .channel(`live-chat-${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_replies',
          filter: `request_id=eq.${conversation.id}`,
        },
        (payload) => {
          const newMsg = payload.new as ChatMessage;
          if (newMsg?.is_staff) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.id]);

  // Online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-3 text-foreground-500">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Đang kết nối...</span>
        </div>
      </div>
    );
  }

  if (error && !conversation) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-chat-off-line text-2xl text-rose-500"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">{error}</p>
          <Link
            to="/dang-nhap"
            className="mt-3 inline-block px-4 py-2 rounded-md bg-primary-500 text-background-950 text-sm font-semibold cursor-pointer hover:bg-primary-600"
          >
            Đăng nhập
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col overflow-hidden min-h-0 ${bare ? '' : 'rounded-xl border border-background-200/60 bg-background-100'}`}>
      {/* Chat header */}
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-3 shrink-0">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-customer-service-2-line text-lg text-primary-600"></i>
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background-100 ${isOnline ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-950">Hỗ trợ viên</p>
          <p className="text-xs text-foreground-500">
            {isOnline ? 'Trực tuyến' : 'Mất kết nối'}
            {conversation && (
              <span className="ml-2 text-foreground-400">· Mã: {conversation.request_code}</span>
            )}
          </p>
        </div>
        <Link
          to="/lien-he"
          className="px-3 py-1.5 rounded-md text-xs font-semibold text-foreground-600 border border-background-300/60 hover:text-primary-400 cursor-pointer transition-colors whitespace-nowrap"
        >
          Gửi yêu cầu
        </Link>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="w-12 h-12 rounded-full bg-background-200 flex items-center justify-center mb-2">
              <i className="ri-chat-3-line text-xl text-foreground-500"></i>
            </div>
            <p className="text-sm text-foreground-500">Bắt đầu cuộc trò chuyện...</p>
          </div>
        )}

        {messages.map((msg, index) => {
          const showDate = index === 0 || formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
          const isMe = !msg.is_staff;

          return (
            <div key={msg.id}>
              {showDate && (
                <div className="flex items-center justify-center my-3">
                  <span className="text-[11px] text-foreground-400 bg-background-200/50 px-3 py-1 rounded-full">
                    {formatDate(msg.created_at)}
                  </span>
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  isMe
                    ? 'bg-primary-500 text-background-950 rounded-br-md'
                    : 'bg-background-200 text-foreground-900 rounded-bl-md'
                }`}>
                  {msg.content && (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  )}
                  {msg.attachment_url && (
                    <a
                      href={msg.attachment_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-2 block text-xs font-semibold underline break-all"
                    >
                      <i className="ri-attachment-2 mr-1"></i>
                      {msg.attachment_name || 'Tệp đính kèm'}
                    </a>
                  )}
                  <p className={`text-[10px] mt-1 ${isMe ? 'text-primary-300' : 'text-foreground-500'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="px-4 py-3 border-t border-background-200/60 shrink-0">
        {error && (
          <p className="text-xs text-rose-500 mb-2">{error}</p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder="Nhập tin nhắn..."
            className="flex-1 px-3 py-2.5 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none min-h-[42px] max-h-[120px]"
            style={{ overflow: 'hidden' }}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            className="w-10 h-10 rounded-full bg-primary-500 text-background-950 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-background-950 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <i className="ri-send-plane-fill text-lg"></i>
            )}
          </button>
        </div>
        <p className="text-[10px] text-foreground-400 mt-1.5">Nhấn Enter để gửi, Shift + Enter để xuống dòng</p>
      </div>
    </div>
  );
}